import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  Icon,
  Link,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { RiDownload2Line, RiSearchLine } from 'react-icons/ri';
import * as Yup from 'yup';
import { DatePicker } from '../../../../../../../components/Form/DatePicker';
import { WaveSideEnum } from '../../../../../../../models/activities';
import { createBookingExternalPaymentsReportFileService } from '../../../../../../../services/Bookings/CreateBookingExternalPaymentsReportFileService';
import { listBookingExternalPaymentReportsService } from '../../../../../../../services/Bookings/ListBookingExternalPaymentReportsService';
import {
  maskDateTime,
  maskMoney,
} from '../../../../../../../utils/formatters/handleMask';
import {
  getResponseErrorMessage,
  translateError,
} from '../../../../../../../utils/errors';
import { useAuth } from '../../../../../../../hooks/auth';
import {
  BookingPaymentsReportTable,
  IBookingPaymentsReportTableItem,
} from '../components/BookingPaymentsReportTable';

interface ILoadBookingExternalPaymentsReports {
  authorizationDateEnd: Date;
  authorizationDateInit: Date;
  page: number;
}

interface IExportInvoiceReportsFormData {
  authorizationDateEnd: Date;
  authorizationDateInit: Date;
}

const bookingExternalPaymentsReportSchema = Yup.object().shape({
  authorizationDateEnd: Yup.date().nullable().required('Requerido'),
  authorizationDateInit: Yup.date()
    .max(Yup.ref('endDate'), 'Data de início deve ser anterior à data final')
    .nullable()
    .required('Requerido'),
});

export const BookingExternalPaymentsReportList = (): JSX.Element => {
  const toast = useToast();

  const { venture } = useAuth();
  const ventureId = venture?.id;

  const [
    bookingExternalPaymentsReportList,
    setBookingExternalPaymentsReportList,
  ] = useState<IBookingPaymentsReportTableItem[]>([]);
  const [downloadLinkUrl, setDownloadLinkUrl] = useState<string>();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number>();

  const downloadLinkRef = useRef<HTMLAnchorElement>(null);

  const maxDate = useMemo(() => new Date(), []);

  const { register, handleSubmit, formState, setValue, control, watch } =
    useForm({
      resolver: yupResolver(bookingExternalPaymentsReportSchema),
    });

  const { errors } = formState;

  const dateEnd = watch('authorizationDateEnd');
  const dateInit = watch('authorizationDateInit');

  const loadBookingExternalPaymentsReports = useCallback(
    async ({
      authorizationDateEnd,
      authorizationDateInit,
      page,
    }: ILoadBookingExternalPaymentsReports) => {
      try {
        const { items: paymentReports, pages } =
          await listBookingExternalPaymentReportsService({
            authorizationDateEnd,
            authorizationDateInit,
            limit: 20,
            order: 'DESC',
            sort: 'createdAt',
            page,
          });

        const parsedPaymentReports =
          paymentReports.map<IBookingPaymentsReportTableItem>((report) => {
            const [bookedYear, bookedMonth, bookedDay] =
              report.bookedDate.split('-');

            return {
              ...report,
              formattedAuthorizedAt: report.authorizedAt
                ? maskDateTime(report.authorizedAt)
                : '-',
              formattedBookedDate:
                report.bookedDate === '-'
                  ? '-'
                  : `${bookedDay}/${bookedMonth}/${bookedYear}`,
              formattedBookedEndTime: report.bookedEndTime.slice(0, 5),
              formattedBookedStartTime: report.bookedStartTime.slice(0, 5),
              formattedPrice: maskMoney(report.itemBilledAmount),
              prettyWaveSide: report.waveSide
                ? WaveSideEnum[report.waveSide]
                : '-',
            };
          });

        setTotalPages(pages);
        setBookingExternalPaymentsReportList(parsedPaymentReports);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar dados',
            description:
              'Ocorreu um erro ao carregar os dados dos pagamentos, tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [toast],
  );

  const handlePageChange = useCallback(
    async (page: number) => {
      setCurrentPage(page);

      await loadBookingExternalPaymentsReports({
        authorizationDateEnd: dateEnd,
        authorizationDateInit: dateInit,
        page,
      });
    },
    [dateEnd, dateInit, loadBookingExternalPaymentsReports],
  );

  useEffect(() => {
    setValue('endDate', maxDate);
  }, [maxDate, setValue]);

  useEffect(() => {
    setBookingExternalPaymentsReportList([]);
    setCurrentPage(1);
    setTotalPages(undefined);
  }, [ventureId]);

  useEffect(() => {
    if (downloadLinkUrl) {
      downloadLinkRef.current?.click();
    }
  }, [downloadLinkUrl]);

  const handleExportInvoiceReports: SubmitHandler<IExportInvoiceReportsFormData> =
    useCallback(
      async ({ authorizationDateEnd, authorizationDateInit }) => {
        try {
          const fileUrl = await createBookingExternalPaymentsReportFileService({
            authorizationDateEnd,
            authorizationDateInit,
            order: 'DESC',
            sort: 'createdAt',
          });

          setDownloadLinkUrl(fileUrl);

          toast({
            title: 'Exportado com sucesso',
            description: 'Relatório foi exportado corretamente',
            status: 'success',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        } catch (err) {
          if (axios.isAxiosError(err) && err.response?.status !== 401) {
            const errorMessage = await getResponseErrorMessage(
              err.response?.data,
            );

            toast({
              title: 'Falha ao exportar',
              description:
                translateError({ message: errorMessage || '' }) ||
                'Ocorreu um erro ao exportar o relatório, tente novamente',
              status: 'error',
              duration: 3000,
              isClosable: true,
              variant: 'subtle',
              position: 'top-right',
            });
          }
        }
      },
      [toast],
    );

  const handleList: SubmitHandler<IExportInvoiceReportsFormData> = useCallback(
    async ({ authorizationDateEnd, authorizationDateInit }) => {
      setCurrentPage(1);

      await loadBookingExternalPaymentsReports({
        authorizationDateEnd,
        authorizationDateInit,
        page: 1,
      });
    },
    [loadBookingExternalPaymentsReports],
  );

  return (
    <Box>
      <Flex
        as="form"
        flexDirection="column"
        onSubmit={handleSubmit(handleList)}
      >
        <ButtonGroup alignSelf="flex-end">
          <Button
            colorScheme="blue"
            size="sm"
            type="submit"
            leftIcon={<Icon as={RiSearchLine} fontSize="md" />}
          >
            <Text>Listar</Text>
          </Button>

          <Link
            hidden
            download={`booking-payments-reports-${Date.now()}.xlsx`}
            ref={downloadLinkRef}
            href={downloadLinkUrl}
          >
            Download
          </Link>
          <Button
            colorScheme="green"
            size="sm"
            onClick={handleSubmit(handleExportInvoiceReports)}
            leftIcon={<Icon as={RiDownload2Line} fontSize="md" />}
          >
            <Text>Exportar</Text>
          </Button>
        </ButtonGroup>

        <VStack spacing="8">
          <SimpleGrid minChildWidth="240px" spacing="8" w="100%">
            <DatePicker
              label="Data de autorização do pagamento de"
              maxDate={maxDate}
              control={control}
              isClearable={false}
              error={errors.authorizationDateInit}
              {...register('authorizationDateInit')}
            />

            <DatePicker
              label="Data de autorização do pagamento até"
              maxDate={maxDate}
              control={control}
              isClearable={false}
              error={errors.authorizationDateEnd}
              {...register('authorizationDateEnd')}
            />
          </SimpleGrid>

          <Divider />
        </VStack>
      </Flex>

      <BookingPaymentsReportTable
        mt="4"
        paymentReports={bookingExternalPaymentsReportList}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        totalPages={totalPages}
      />
    </Box>
  );
};
